import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, LinearProgress, Pagination, Snackbar, Tab, Tabs, Typography } from '@mui/material'
import { useNamesTranslation } from '../hooks/useNamesTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import useAuthorization from '../../generic/hooks/useAuthorization/useAuthorization'
import { hasTarget } from '../model/timelineQuery'
import { buildTimelineView, filterSuspicious, hideFinishedJobs } from '../model/timelineView'
import { edgeOf } from '../model/correctionDraft'
import { stepOnGrid } from '../model/gridInstant'
import { findByIdentity, highlightedLaneKind, highlightedRowIds, identityOf } from '../model/interventionIndex'
import { useCatalogs } from '../hooks/useCatalogs'
import { useCorrection } from '../hooks/useCorrection'
import { useHistory } from '../hooks/useHistory'
import { useTimeline } from '../hooks/useTimeline'
import CorrectionHistory from './CorrectionHistory'
import CorrectionPanel from './CorrectionPanel'
import TimelineGrid from './TimelineGrid'
import TimelineLegend from './TimelineLegend'
import TimelineToolbar from './TimelineToolbar'

// The selected intervention is kept by its identity (anchor + worker), so that a new reading finds it wherever it is (RN42).
const NO_SELECTION = { table: '', rowId: 0, workerId: 0, sessionKey: '' }

// «Machines 6–7 of 7»: the slice of the page over the machines the worker has (RN14).
const pagerRange = ({ number, size, totalDevices }) => ({
  from: number * size + 1,
  to: Math.min(totalDevices, (number + 1) * size),
  total: totalDevices
})

const NOT_APPLIED = { open: false, correctionId: '' }
const NOT_REVERTED = { open: false }

// The id of the correction a 201 of POST dataCorrection created (`corrections[]`, #201 §3), '' when it brings none. A «close
// now» creates two, [close, trim]: «View history» opens the LAST one, the trim, which shows the group (story 211, RN28).
const appliedIdOf = (result) => (result && Array.isArray(result.corrections) && result.corrections.length > 0 ? result.corrections[result.corrections.length - 1].id : '')

// «Workday correction» (story 202): review the timelines of machines and workers and correct an intervention; the second tab
// is the history of the corrections (#207, #205).
const DataCorrectionPage = () => {
  const { t_ } = useNamesTranslation()
  const { formatDateTime } = useDateFormatter()
  const isUserAuthorized = useAuthorization()
  // Applying is enabled by the permission alone: there is no plant switch (DPO-1).
  const canRequest = isUserAuthorized('DATA_CORRECTION_REQUEST')
  const canView = isUserAuthorized('DATA_CORRECTION_VIEW')
  const [tab, setTab] = useState('timelines')
  const deviceNameRef = useRef(() => '')
  const timeline = useTimeline({ deviceNameRef })
  const catalogs = useCatalogs(timeline.loaded.response, { canRequest })
  deviceNameRef.current = catalogs.deviceNameOf
  // After a revert (story 211, RN13): the notice (no correction id, PO 2026-10-17), and the timeline read again
  // only if one is loaded; the detail and page 0 of the history are read by `useHistory`.
  const [reverted, setReverted] = useState(NOT_REVERTED)
  const handleReverted = useCallback((result) => {
    setReverted({ open: true })
    if (timeline.loaded.response) timeline.handleReload()
  }, [timeline.loaded.response, timeline.handleReload]) // eslint-disable-line react-hooks/exhaustive-deps
  const history = useHistory({ active: tab === 'history', onReverted: handleReverted })
  const [zoomDays, setZoomDays] = useState(2)
  const [onlySuspicious, setOnlySuspicious] = useState(false)
  const [showFinished, setShowFinished] = useState(false)
  const [selected, setSelected] = useState(NO_SELECTION)
  const [applied, setApplied] = useState(NOT_APPLIED)
  const sessionCounter = useRef(0)
  // While a correction is being applied nothing outside the panel may change the selection, the draft or the query (RN66).
  const applyingRef = useRef(false)
  const { query, loaded } = timeline

  // What is painted is what the LAST answer said, with the query that asked for it (so the range, the grouping and the
  // worker filter are the ones of that answer even while a newer request is in flight).
  const fullView = useMemo(() => {
    if (!loaded.response) return undefined
    return buildTimelineView(loaded.response, {
      view: loaded.query.view,
      workerFilter: loaded.query.workerId,
      t_,
      range: { fromMs: loaded.query.from, toMs: loaded.query.to },
      selectable: canRequest
    })
  }, [loaded, t_, canRequest])
  // The switch hides the blocks without suspicious rows in their visible lanes; it asks nothing and keeps the page (RN30).
  const suspiciousView = useMemo(() => (fullView && onlySuspicious ? filterSuspicious(fullView) : fullView), [fullView, onlySuspicious])

  // The selected intervention, looked up by its identity in what is painted. A reading that still has it keeps the panel and its
  // draft; one that no longer has it closes the panel and drops the draft (RN42). Except while it is being applied: then the
  // panel keeps the intervention as it was last read, and when the apply ends a 201 closes it (S89) and an error leaves it
  // read-only until the user closes it (`heldSession`, A202-23).
  const [heldSession, setHeldSession] = useState('')
  const lastFound = useRef({ sessionKey: '' })
  const found = useMemo(() => (fullView && selected.sessionKey ? findByIdentity(fullView.blocks, selected) : {}), [fullView, selected])
  if (found.intervention) lastFound.current = { sessionKey: selected.sessionKey, ...found }
  const inReading = Boolean(found.intervention)
  const kept = !inReading && selected.sessionKey !== '' && lastFound.current.sessionKey === selected.sessionKey &&
    (applyingRef.current || heldSession === selected.sessionKey)
  const selectedBlock = kept ? lastFound.current.block : found.block
  const selectedIntervention = kept ? lastFound.current.intervention : found.intervention
  const lost = Boolean(selected.sessionKey && fullView && !selectedIntervention)
  useEffect(() => { if (lost) setSelected(NO_SELECTION) }, [lost])
  const selection = useMemo(() => (selectedIntervention
    ? {
        blockKey: selectedBlock.key,
        laneKind: highlightedLaneKind(selectedIntervention),
        workerId: selectedIntervention.worker.id,
        rowIds: highlightedRowIds(selectedIntervention)
      }
    : undefined), [selectedIntervention, selectedBlock])

  const fullViewRef = useRef(fullView)
  fullViewRef.current = fullView
  const handleSelect = useCallback((blockKey, interventionIndex) => {
    if (applyingRef.current) return
    const block = fullViewRef.current.blocks.find((candidate) => candidate.key === blockKey)
    sessionCounter.current += 1
    setSelected({ ...identityOf(block.interventions[interventionIndex]), sessionKey: String(sessionCounter.current) })
  }, [])
  const handleClosePanel = useCallback(() => setSelected(NO_SELECTION), [])
  const handleReload = useCallback(() => {
    setSelected(NO_SELECTION)
    timeline.handleReload()
  }, [timeline.handleReload]) // eslint-disable-line react-hooks/exhaustive-deps
  // After applying, the timeline is read again and the history goes back to its page 0 (R8 of #207: a row could repeat or be
  // skipped between two pages otherwise); the notice can open the new correction in the history.
  const handleApplied = useCallback((result) => {
    setSelected(NO_SELECTION)
    setApplied({ open: true, correctionId: appliedIdOf(result) })
    timeline.handleReload()
    history.handleReloadFirstPage()
  }, [timeline.handleReload, history.handleReloadFirstPage]) // eslint-disable-line react-hooks/exhaustive-deps
  // «already corrected» in the panel (#3068): that correction, in the history.
  const handleOpenCorrection = useCallback((correctionId) => {
    setTab('history')
    history.handleSelect(correctionId)
  }, [history.handleSelect]) // eslint-disable-line react-hooks/exhaustive-deps
  // «View on the timeline» of a correction (RN16): the timelines tab, by machine and worker, in a period that shows its stretch;
  // no intervention stays selected and the zoom is not touched. Not while a correction is being applied (RN66).
  const handleShowInTimeline = useCallback((target) => {
    if (applyingRef.current) return
    setSelected(NO_SELECTION)
    timeline.handleShowCorrection(target)
    setTab('timelines')
  }, [timeline.handleShowCorrection]) // eslint-disable-line react-hooks/exhaustive-deps
  const handleViewHistory = useCallback(() => {
    setApplied((current) => ({ ...current, open: false }))
    setTab('history')
    if (applied.correctionId) history.handleSelect(applied.correctionId)
  }, [applied.correctionId, history.handleSelect]) // eslint-disable-line react-hooks/exhaustive-deps

  const correction = useCorrection({
    selected: selectedIntervention ? { sessionKey: selected.sessionKey, block: selectedBlock, intervention: selectedIntervention } : {},
    inReading,
    // The «now» of the reading: the recorded end of an open intervention (story 211, RN20).
    nowMs: fullView ? fullView.nowMs : 0,
    canRequest,
    reasons: catalogs.reasons,
    reasonsLoaded: !catalogs.reasonsFailed,
    t_,
    format: formatDateTime,
    lookupWorkerName: catalogs.workerNameOf,
    lookupDeviceName: catalogs.deviceNameOf,
    onApplied: handleApplied
  })

  // The handles are driven through a ref so that their callbacks never change: only the lanes with something to draw repaint (RN36).
  const correctionRef = useRef(correction)
  correctionRef.current = correction
  applyingRef.current = correction.applying
  // When an apply ends with an error and the reading no longer has the intervention, its panel stays (read-only); a reading
  // that brings it back releases it (A202-23).
  useEffect(() => {
    if (!correction.applying && selected.sessionKey !== '' && !inReading && lastFound.current.sessionKey === selected.sessionKey) setHeldSession(selected.sessionKey)
    else if (inReading) setHeldSession('')
  }, [correction.applying, inReading, selected.sessionKey])
  const handleDrag = useCallback((kind, ms) => { if (!correctionRef.current.applying) correctionRef.current.setDragged(kind, ms) }, [])
  const handleNudge = useCallback((kind, direction, big) => {
    if (correctionRef.current.applying) return
    const { draft, intervention } = correctionRef.current
    const current = draft.kind === kind && Number.isFinite(draft.newInstant) ? draft.newInstant : edgeOf(kind, intervention)
    correctionRef.current.setDragged(kind, stepOnGrid(current, direction, big ? 12 : 1))
  }, [])

  // What the timeline draws for the open correction: the band of the stretch (RN41) and the handles (RN40).
  const overlay = useMemo(() => {
    if (!correction.active) return undefined
    const { intervention, draft } = correction
    const workerId = intervention.worker.id
    const bandLaneKeys = [`imputations-${workerId}`]
    if (draft.tapes.includes('PRESENCE')) bandLaneKeys.push(`presence-${workerId}`)
    if (draft.tapes.includes('JOB_STATE') && intervention.job) {
      bandLaneKeys.push(`job-${intervention.job.id}`)
      if (selectedBlock.raw.device.mode === 'OFFLINE') bandLaneKeys.push('status')
    }
    const handles = correction.handlesShown
      ? ['ADJUST_START', 'ADJUST_END']
          .filter((kind) => intervention.allowedKinds.includes(kind))
          .map((kind) => ({ kind, valueMs: draft.kind === kind && Number.isFinite(draft.newInstant) ? draft.newInstant : edgeOf(kind, intervention) }))
      : undefined
    return { workerId, bandLaneKeys, band: correction.window, handles }
  }, [correction.active, correction.intervention, correction.draft, correction.handlesShown, correction.window, selectedBlock]) // eslint-disable-line react-hooks/exhaustive-deps

  // The finished jobs are hidden unless the switch shows them (PO 2026-10-17); the job of the selected intervention never is.
  const keptJobLane = selectedIntervention && selectedIntervention.job ? `${selectedBlock.key}|job-${selectedIntervention.job.id}` : ''
  const view = useMemo(() => (suspiciousView && !showFinished ? hideFinishedJobs(suspiciousView, { t_, keepLaneKeys: [keptJobLane] }) : suspiciousView), [suspiciousView, showFinished, t_, keptJobLane])

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant='h5' component='h1'>{t_('Workday correction')}</Typography>
      <Typography color='text.secondary' sx={{ mb: 1.5, maxWidth: '70ch' }}>
        {t_('Review the timelines of workers and machines and correct the wrongly recorded clock-ins.')}
      </Typography>
      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value='timelines' label={t_('Timelines')} />
        {canView && (
          <Tab
            value='history'
            data-testid='history-tab'
            label={<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>{t_('Correction history')}<Chip size='small' label={history.totalElements} data-testid='history-count' /></Box>}
          />
        )}
      </Tabs>
      {/* The timelines stay mounted while the history is shown, so that coming back keeps the period, the filters and the zoom. */}
      <Box hidden={tab !== 'timelines'} sx={{ display: tab === 'timelines' ? 'block' : 'none' }}>
        <TimelineToolbar
          query={query}
          zoomDays={zoomDays}
          onlySuspicious={onlySuspicious}
          showFinished={showFinished}
          deviceOptions={catalogs.deviceOptions}
          workerOptions={catalogs.workerOptions}
          canGoNext={timeline.canGoNext}
          disabled={correction.applying}
          onPrevious={timeline.handlePrevious}
          onNext={timeline.handleNext}
          onView={timeline.handleView}
          onFilters={timeline.handleFilters}
          onZoom={setZoomDays}
          onOnlySuspicious={setOnlySuspicious}
          onShowFinished={setShowFinished}
        />
        {catalogs.messages.map((message) => <Alert key={message.key + JSON.stringify(message.params)} severity='warning' sx={{ mb: 1 }}>{t_(message.key, message.params)}</Alert>)}
        {timeline.messages.notice.map((message) => <Alert key={message.key} severity='info' sx={{ mb: 1 }}>{t_(message.key, message.params)}</Alert>)}
        {timeline.messages.errors.map((message) => <Alert key={message.key} severity='error' sx={{ mb: 1 }}>{t_(message.key, message.params)}</Alert>)}
        <TimelineLegend />
        {!hasTarget(query) && <Typography color='text.secondary'>{t_('Choose a machine or a worker to see their timeline.')}</Typography>}
        {hasTarget(query) && timeline.loading && <LinearProgress />}
        {hasTarget(query) && loaded.response && loaded.response.page.totalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant='body2'>
              {t_('Machines {{from}}–{{to}} of {{total}}', pagerRange(loaded.response.page))}
            </Typography>
            <Pagination size='small' count={loaded.response.page.totalPages} page={loaded.response.page.number + 1} onChange={(_event, page) => timeline.handlePage(page - 1)} />
          </Box>
        )}
        {hasTarget(query) && view && view.blocks.length === 0 && <Typography color='text.secondary'>{t_('No activity with these filters.')}</Typography>}
        {hasTarget(query) && view && view.blocks.length > 0 && (
          <TimelineGrid
            view={view}
            range={{ fromMs: loaded.query.from, toMs: loaded.query.to }}
            zoomDays={zoomDays}
            dimmed={timeline.loading}
            selection={selection}
            overlay={overlay}
            focus={timeline.focus}
            onSelect={handleSelect}
            onDrag={handleDrag}
            onNudge={handleNudge}
          />
        )}
        {hasTarget(query) && view && (
          // The help under the timeline (RN35): the one that invites to click and drag is only for who can correct (RN5).
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            {canRequest
              ? t_('Several workers can be on the same machine at the same time, even on several jobs in parallel; each one has a presence row and a clock-in row. Click the presence or a clock-in of a worker to correct their intervention; with one selected, drag its white edges inwards. The case (A1, AN, B1, BN) appears when hovering over a clock-in.')
              : t_('Several workers can be on the same machine at the same time, even on several jobs in parallel; each one has a presence row and a clock-in row. The case (A1, AN, B1, BN) appears when hovering over a clock-in.')}
          </Typography>
        )}
      </Box>
      {canView && tab === 'history' && <CorrectionHistory history={history} deviceOptions={catalogs.deviceOptions} workerOptions={catalogs.workerOptions} canRequest={canRequest} onShowInTimeline={handleShowInTimeline} />}
      {correction.active && tab === 'timelines' && (
        <CorrectionPanel
          block={selectedBlock}
          correction={correction}
          loaded={loaded}
          nowMs={fullView.nowMs}
          reasons={catalogs.reasons}
          reasonsFailed={catalogs.reasonsFailed}
          onClose={handleClosePanel}
          onReload={handleReload}
          onOpenCorrection={canView ? handleOpenCorrection : undefined}
        />
      )}
      <Snackbar
        open={applied.open} autoHideDuration={6000} onClose={() => setApplied((current) => ({ ...current, open: false }))} message={t_('Correction applied')}
        action={canView ? <Button color='primary' size='small' onClick={handleViewHistory}>{t_('View history')}</Button> : undefined}
      />
      <Snackbar
        open={reverted.open} autoHideDuration={6000} onClose={() => setReverted((current) => ({ ...current, open: false }))}
        message={t_('Correction reverted. The timelines go back to their previous state.')}
      />
    </Box>
  )
}

export default DataCorrectionPage
